import React, { useState, useEffect } from 'react';

// Helpers
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';
import { generateHeaderForQuestion } from 'Shared/utils/Reviews.utils';

// Components
import { WpContainer, WpContainerHeader, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpGifPicker, WpGif } from 'Wpds/components/gif';
import { WpCollapsible } from 'Wpds/components/collapsible';
import PrivateQuestionPopover from '../ReviewQuestion/PrivateQuestionPopover';

// Styles
import './GifQuestion.scss';

type Props = {
  receiverName: string;
  question;
  isOpen: boolean;
  handleMoveNext?: () => void;
  handleMoveBack?: () => void;
  answers;
  handleSetAnswers: Function;
  isSummaryQuestion?: boolean;
  isPreview?: boolean;
};

const GifQuestion = ({
  receiverName,
  question,
  isOpen,
  handleMoveNext,
  handleMoveBack,
  answers,
  handleSetAnswers,
  isPreview = false,
}: Props) => {
  const [gifData, setGifData] = useState(null);
  const questionKey = ReviewFormTemplatesHelpers.getQuestionKey(question);
  const header = generateHeaderForQuestion({ question });

  const hasGif = Boolean(answers[questionKey]?.gif_url);
  const isNextDisabled = isPreview ? false : !gifData && !hasGif;
  const showBadge = ReviewFormTemplatesHelpers.getQuestionPrivate(question);
  const showOptional = ReviewFormTemplatesHelpers.getQuestionOptional(question);

  const handleSaveAnswers = () => {
    if (gifData) {
      const data = {
        [questionKey]: {
          gif_url: gifData.images.original.url,
        },
      };
      handleSetAnswers(data);
    }
  };

  const handleSelectGif = gifData => {
    setGifData(gifData);
  };

  const handleRemoveGif = () => {
    setGifData(null);
    const data = {
      [questionKey]: {},
    };
    handleSetAnswers(data);
  };

  useEffect(() => {
    handleSaveAnswers();
  }, [gifData, hasGif]);

  const baseClassName = 'GifQuestion';

  return (
    <WpContainer className={baseClassName}>
      <WpContainerHeader>
        <div className={`${baseClassName}__header-container`}>
          <span>
            {header} {showOptional && <span>(Optional)</span>}
          </span>

          {showBadge && <PrivateQuestionPopover receiverName={receiverName}></PrivateQuestionPopover>}
        </div>
      </WpContainerHeader>
      <WpCollapsible open={isOpen}>
        <WpContainerBody>
          {hasGif || gifData ? (
            <div className={`${baseClassName}__gif`}>
              <div className={`${baseClassName}__gif__btn`}>
                <WpButton onClick={handleRemoveGif} variant="tertiary">
                  Change GIF
                </WpButton>
              </div>
              <WpGif url={answers[questionKey]?.gif_url || gifData.images.original.url} />
            </div>
          ) : (
            <WpGifPicker onSelect={handleSelectGif} initialSearchTerm="rock star" autoFocus={false} />
          )}
        </WpContainerBody>
        {(handleMoveBack || handleMoveNext) && (
          <WpContainerFooter justifyContent="flexEnd">
            <WpButtonGroup>
              {handleMoveBack && (
                <WpButton onClick={handleMoveBack} variant="tertiary">
                  Back
                </WpButton>
              )}
              {handleMoveNext && (
                <WpButton onClick={handleMoveNext} disabled={isNextDisabled}>
                  Next
                </WpButton>
              )}
            </WpButtonGroup>
          </WpContainerFooter>
        )}
      </WpCollapsible>
    </WpContainer>
  );
};

export default GifQuestion;
